import { Vector3 } from "@babylonjs/core/Maths/math.vector";
export class BufferedRigidBodyImpl {
    shouldSync;
    _isDirty;
    /**
     * for setTransformMatrix
     */
    _motionStateMatrixBuffer;
    _isMotionStateMatrixBufferDirty;
    /**
     * for setDynamicTransformMatrix
     */
    _dynamicTransformMatrixBuffer;
    _isDynamicTransformMatrixBufferDirty;
    /**
     * for effectiveKinematicState
     */
    _kinematicState;
    _isKinematicStateDirty;
    _commandBuffer;
    constructor() {
        this.shouldSync = false;
        this._isDirty = false;
        this._motionStateMatrixBuffer = new Float32Array(16);
        this._isMotionStateMatrixBufferDirty = false;
        this._dynamicTransformMatrixBuffer = null;
        this._isDynamicTransformMatrixBufferDirty = false;
        this._kinematicState = false;
        this._isKinematicStateDirty = false;
        this._commandBuffer = new Map();
    }
    get needToCommit() {
        return this._isDirty;
    }
    commitToWasm(wasmInstance, bodyPtr, motionStatePtr, kinematicStatePtr, worldTransformPtr) {
        if (!this._isDirty) {
            return;
        }
        if (this._isMotionStateMatrixBufferDirty) {
            const m = this._motionStateMatrixBuffer;
            const n = motionStatePtr.array;
            n[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 0] = m[0];
            n[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 0] = m[1];
            n[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 0] = m[2];
            n[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 1] = m[4];
            n[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 1] = m[5];
            n[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 1] = m[6];
            n[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 2] = m[8];
            n[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 2] = m[9];
            n[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 2] = m[10];
            n[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 0] = m[12];
            n[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 1] = m[13];
            n[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 2] = m[14];
            const kinematicState = kinematicStatePtr.array;
            if ((kinematicState[0] & 240 /* TemporalKinematicState.ReadMask */) !== 0 /* TemporalKinematicState.Disabled */) {
                kinematicState[0] = (kinematicState[0] & 15 /* TemporalKinematicState.WriteMask */) | 32 /* TemporalKinematicState.WaitForChange */;
            }
            this._isMotionStateMatrixBufferDirty = false;
        }
        if (this._isDynamicTransformMatrixBufferDirty) {
            const m = this._dynamicTransformMatrixBuffer;
            const n = worldTransformPtr.array;
            n[0 /* BtTransformOffsets.MatrixRowX */ + 0] = m[0];
            n[16 /* BtTransformOffsets.MatrixRowY */ + 0] = m[1];
            n[32 /* BtTransformOffsets.MatrixRowZ */ + 0] = m[2];
            n[0 /* BtTransformOffsets.MatrixRowX */ + 1] = m[4];
            n[16 /* BtTransformOffsets.MatrixRowY */ + 1] = m[5];
            n[32 /* BtTransformOffsets.MatrixRowZ */ + 1] = m[6];
            n[0 /* BtTransformOffsets.MatrixRowX */ + 2] = m[8];
            n[16 /* BtTransformOffsets.MatrixRowY */ + 2] = m[9];
            n[32 /* BtTransformOffsets.MatrixRowZ */ + 2] = m[10];
            n[48 /* BtTransformOffsets.Translation */ + 0] = m[12];
            n[48 /* BtTransformOffsets.Translation */ + 1] = m[13];
            n[48 /* BtTransformOffsets.Translation */ + 2] = m[14];
            this._isDynamicTransformMatrixBufferDirty = false;
        }
        if (this._isKinematicStateDirty) {
            const kinematicState = kinematicStatePtr.array;
            if (this._kinematicState) {
                kinematicState[0] = (kinematicState[0] & 240 /* KinematicToggleState.WriteMask */) | 1 /* KinematicToggleState.Enabled */;
            }
            else {
                kinematicState[0] = (kinematicState[0] & 240 /* KinematicToggleState.WriteMask */) | 0 /* KinematicToggleState.Disabled */;
            }
            this._isKinematicStateDirty = false;
        }
        for (const [command, args] of this._commandBuffer) {
            switch (command) {
                case 0 /* RigidBodyCommand.SetDamping */:
                    wasmInstance.rigidBodySetDamping(bodyPtr, args[0], args[1]);
                    break;
                case 1 /* RigidBodyCommand.SetMassProps */: {
                    const localInertia = args[1];
                    wasmInstance.rigidBodySetMassProps(bodyPtr, args[0], localInertia.x, localInertia.y, localInertia.z);
                    break;
                }
                case 2 /* RigidBodyCommand.Translate */: {
                    const translation = args[0];
                    wasmInstance.rigidBodyTranslate(bodyPtr, translation.x, translation.y, translation.z);
                    break;
                }
                case 3 /* RigidBodyCommand.SetLinearVelocity */: {
                    const linearVelocity = args[0];
                    wasmInstance.rigidBodySetLinearVelocity(bodyPtr, linearVelocity.x, linearVelocity.y, linearVelocity.z);
                    break;
                }
                case 4 /* RigidBodyCommand.SetAngularVelocity */: {
                    const angularVelocity = args[0];
                    wasmInstance.rigidBodySetAngularVelocity(bodyPtr, angularVelocity.x, angularVelocity.y, angularVelocity.z);
                    break;
                }
            }
        }
        this._commandBuffer.clear();
        this._isDirty = false;
    }
    setTransformMatrixFromArray(_motionStatePtr, _kinematicStatePtr, array, offset) {
        this._motionStateMatrixBuffer.set(array, offset);
        this._isMotionStateMatrixBufferDirty = true;
        this._isDirty = true;
    }
    setDynamicTransformMatrixFromArray(_worldTransformPtr, array, offset) {
        if (this._dynamicTransformMatrixBuffer === null) {
            this._dynamicTransformMatrixBuffer = new Float32Array(16);
        }
        this._dynamicTransformMatrixBuffer.set(array, offset);
        this._isDynamicTransformMatrixBufferDirty = true;
        this._isDirty = true;
    }
    getEffectiveKinematicState(_kinematicStatePtr) {
        // we can't access _kinematicStatesPtr directly here
        // because it accessed from wasm side for read/write temporal kinematic state
        return this._kinematicState;
    }
    setEffectiveKinematicState(_kinematicStatePtr, value) {
        if (this._kinematicState === value) {
            return;
        }
        this._kinematicState = value;
        this._isKinematicStateDirty = true;
        this._isDirty = true;
    }
    setDamping(_wasmInstance, _bodyPtr, linearDamping, angularDamping) {
        this._commandBuffer.set(0 /* RigidBodyCommand.SetDamping */, [linearDamping, angularDamping]);
        this._isDirty = true;
    }
    // this member is not updated by wasm so no need to synchronization before read
    getLinearDamping(wasmInstance, bodyPtr) {
        return wasmInstance.rigidBodyGetLinearDamping(bodyPtr);
    }
    // this member is not updated by wasm so no need to synchronization before read
    getAngularDamping(wasmInstance, bodyPtr) {
        return wasmInstance.rigidBodyGetAngularDamping(bodyPtr);
    }
    setMassProps(_wasmInstance, _bodyPtr, mass, localInertia) {
        this._commandBuffer.set(1 /* RigidBodyCommand.SetMassProps */, [mass, localInertia.clone()]);
        this._isDirty = true;
    }
    // this member is not updated by wasm so no need to synchronization before read
    getMass(wasmInstance, bodyPtr) {
        return wasmInstance.rigidBodyGetMass(bodyPtr);
    }
    // this member is not updated by wasm so no need to synchronization before read
    getLocalInertia(wasmInstance, bodyPtr) {
        const outBufferPtr = wasmInstance.allocateBuffer(3 * 4 /* Constants.A32BytesPerElement */);
        const outBuffer = wasmInstance.createTypedArray(Float32Array, outBufferPtr, 3).array;
        wasmInstance.rigidBodyGetLocalInertia(bodyPtr, outBufferPtr);
        const result = new Vector3(outBuffer[0], outBuffer[1], outBuffer[2]);
        wasmInstance.deallocateBuffer(outBufferPtr, 3 * 4 /* Constants.A32BytesPerElement */);
        return result;
    }
    translate(_wasmInstance, _bodyPtr, translation) {
        this._commandBuffer.set(2 /* RigidBodyCommand.Translate */, [translation.clone()]);
        this._isDirty = true;
    }
    setLinearVelocity(linearVelocity) {
        this._commandBuffer.set(3 /* RigidBodyCommand.SetLinearVelocity */, [linearVelocity.clone()]);
        this._isDirty = true;
    }
    setAngularVelocity(angularVelocity) {
        this._commandBuffer.set(4 /* RigidBodyCommand.SetAngularVelocity */, [angularVelocity.clone()]);
        this._isDirty = true;
    }
}
